import React, { useState } from 'react';
import { Grid, CircularProgress, Tabs, Tab } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import useExtractedPurchaseOrder, {
  ExtractionResultDTO,
} from 'src/api/core/inbound-emails/useExtractedPurchaseOrder';

interface ExtractedDataTabProps {
  inboundEmailId?: string;
  hasAttachments: boolean;
}

const ExtractedDataTab: React.FC<ExtractedDataTabProps> = ({
  inboundEmailId,
  hasAttachments,
}) => {
  const [selectedExtraction, setSelectedExtraction] = useState<number>(0);

  const {
    data: extractionData,
    isLoading: isLoadingExtraction,
    error: extractionError,
  } = useExtractedPurchaseOrder(inboundEmailId || '', !!inboundEmailId);

  console.log({ extractionData });

  if (!hasAttachments) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          No attachments found
        </MDTypography>
      </MDBox>
    );
  }

  if (!inboundEmailId) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          No email selected
        </MDTypography>
      </MDBox>
    );
  }

  if (isLoadingExtraction) {
    return (
      <MDBox display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (extractionError) {
    return (
      <MDBox
        p={3}
        border="1px solid"
        borderColor="error.main"
        borderRadius="borderRadius"
        color="error.main"
      >
        <MDTypography variant="body2">
          Error loading extracted data: {extractionError.message}
        </MDTypography>
      </MDBox>
    );
  }

  if (!extractionData?.extractions?.length) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          No extracted data found
        </MDTypography>
      </MDBox>
    );
  }

  const renderExtractionDetails = (extraction: ExtractionResultDTO) => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox mb={2}>
              <MDTypography variant="subtitle2" mb={1}>
                Extraction Details
              </MDTypography>
              <MDTypography variant="body2">
                Model: {extraction.modelUsed}
              </MDTypography>
              <MDTypography variant="body2">
                Status: {extraction.status}
              </MDTypography>
              <MDTypography variant="body2">
                Created: {new Date(extraction.createdAt).toLocaleString()}
              </MDTypography>
            </MDBox>
          </Grid>

          {extraction.classification && (
            <Grid item xs={12}>
              <MDBox mb={2}>
                <MDTypography variant="subtitle2" mb={1}>
                  Classification
                </MDTypography>
                <MDTypography variant="body2">
                  Result: {extraction.classification.classificationResult}
                </MDTypography>
                <MDTypography variant="body2">
                  Confidence: {extraction.classification.confidenceScore}
                </MDTypography>
                <MDTypography variant="body2">
                  Model: {extraction.classification.modelUsed}
                </MDTypography>
              </MDBox>
            </Grid>
          )}

          {extraction.errorDetails && (
            <Grid item xs={12}>
              <MDBox mb={2}>
                <MDTypography variant="subtitle2" mb={1}>
                  Error Details
                </MDTypography>
                <pre
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {JSON.stringify(extraction.errorDetails, null, 2)}
                </pre>
              </MDBox>
            </Grid>
          )}

          {extraction.extractedData && (
            <Grid item xs={12}>
              <MDBox mb={2}>
                <MDTypography variant="subtitle2" mb={1}>
                  Extracted Data
                </MDTypography>
                <pre
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {JSON.stringify(extraction.extractedData, null, 2)}
                </pre>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </MDBox>
    );
  };

  return (
    <MDBox p={2}>
      <Tabs
        value={selectedExtraction}
        onChange={(_, newValue) => setSelectedExtraction(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
        }}
      >
        {extractionData.extractions.map((extraction, index) => (
          <Tab
            key={extraction.id}
            label={`Extraction ${index + 1}`}
            value={index}
          />
        ))}
      </Tabs>

      {renderExtractionDetails(extractionData.extractions[selectedExtraction])}
    </MDBox>
  );
};

export default ExtractedDataTab;
